// File: src/components/SimilarTradesCarousel.jsx
// ✅ DROP-IN (Firestore v9 modular)
// ✅ Two-row premium shelf (Alibaba-like):
//    Row 1: Similar to this listing (category + locality bias)
//    Row 2: Recommended for you (preferences/AI overlay if signed in; falls back to trending if guest)
// ✅ Source: PublicTrades only (safe)
// ✅ Excludes current trade
// ✅ Avoids brittle composite indexes by doing ONE latest fetch + client-side scoring

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { db } from "../firebase";
import { collection, doc, getDoc, getDocs, limit, orderBy, query } from "firebase/firestore";
import { getTradeImageSrc, handleImgError } from "../utils/images";

// ---------------- helpers ----------------
const normStr = (v) => String(v ?? "").trim();
const safeLower = (v) => String(v ?? "").trim().toLowerCase();

const DEFAULT_WEIGHTS = {
  category: 2,
  style: 2,
  condition: 1,
  tradeType: 1,
  location: 1,
  brand: 1,
};

const pickCashValue = (trade) => {
  const candidates = [
    trade?.expectedCashAmount,
    trade?.cashExpected,
    trade?.cashPrice,
    trade?.price,
    trade?.estimatedValue,
  ];
  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

const moneyZAR = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "";
  return num.toLocaleString("en-ZA", { style: "currency", currency: "ZAR" });
};

const normalizeArr = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const isProbablyAvailable = (t) => {
  if (!t) return false;
  if (t.isAvailable === false) return false;
  const st = safeLower(t.status || "");
  if (st === "closed" || st === "sold") return false;
  return true;
};

const detectRegionFromLanguage = () => {
  const lang = (typeof navigator !== "undefined" && navigator.language) || "";
  const parts = lang.split("-");
  const region = parts.length >= 2 ? parts[1].toUpperCase() : "";
  return region || null;
};

const countrySignals = (trade) => {
  const raw =
    trade?.countryCode ??
    trade?.country ??
    trade?.locationCountry ??
    trade?.location?.countryCode ??
    trade?.location?.country;
  const sigs = [];
  if (typeof raw === "string" && raw.trim()) sigs.push(raw.trim());
  if (typeof trade?.location === "string" && trade.location.trim()) sigs.push(trade.location.trim());
  return sigs;
};

const isCountryMatch = (trade, regionCode) => {
  if (!regionCode) return false;
  const rc = String(regionCode).toUpperCase();
  const sigs = countrySignals(trade).map((s) => s.toUpperCase());
  if (sigs.some((s) => s === rc)) return true;

  const common = {
    ZA: ["SOUTH AFRICA", "RSA"],
    US: ["UNITED STATES", "USA", "AMERICA"],
    GB: ["UNITED KINGDOM", "UK", "GREAT BRITAIN", "ENGLAND"],
    AE: ["UNITED ARAB EMIRATES", "UAE"],
    SA: ["SAUDI ARABIA"],
  };
  const names = common[rc] || [];
  if (names.length && sigs.some((s) => names.some((n) => s.includes(n)))) return true;
  if (sigs.some((s) => s.includes(rc))) return true;
  return false;
};

const SimilarTradesCarousel = ({
  currentTrade = null,
  uid = null, // pass currentUser?.uid for personalized row
  maxSimilar = 10,
  maxRecommended = 12,
  title = "More options for you",
  subtitle = "Similar listings and personalized picks",
  preferSameCity = true,
  className = "",
}) => {
  const [loading, setLoading] = useState(false);
  const [similar, setSimilar] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [usedAISuggestions, setUsedAISuggestions] = useState(false);

  const [region, setRegion] = useState(null);

  const simRef = useRef(null);
  const recRef = useRef(null);

  const tradeId = normStr(currentTrade?.id);
  const category = normStr(currentTrade?.category || "");
  const categoryNorm = safeLower(currentTrade?.categoryNorm || category);
  const city = normStr(currentTrade?.location?.city || "");
  const country = normStr(currentTrade?.location?.country || "");

  useEffect(() => {
    setRegion(detectRegionFromLanguage());
  }, []);

  const scroll = useCallback((which, dir) => {
    const el = which === "sim" ? simRef.current : recRef.current;
    if (!el) return;
    const amount = Math.max(280, Math.round(el.clientWidth * 0.9));
    el.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  }, []);

  const fetchLatestPublicTrades = useCallback(async () => {
    // Prefer ordered fetch; fallback to limit-only if your dataset lacks createdAt index/field consistency.
    try {
      const q1 = query(collection(db, "PublicTrades"), orderBy("createdAt", "desc"), limit(320));
      const s1 = await getDocs(q1);
      return s1.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch {
      const q2 = query(collection(db, "PublicTrades"), limit(320));
      const s2 = await getDocs(q2);
      return s2.docs.map((d) => ({ id: d.id, ...d.data() }));
    }
  }, []);

  const loadPrefs = useCallback(async () => {
    if (!uid) return { prefs: null, appliedAI: false };

    const prefSnap = await getDoc(doc(db, "preferences", uid));
    if (!prefSnap.exists()) return { prefs: null, appliedAI: false };
    let prefs = prefSnap.data() || {};

    // Optional AI overlay (kept aligned with your current approach)
    let appliedAI = false;
    try {
      const aiSnap = await getDoc(doc(db, "aiSuggestions", uid));
      if (aiSnap.exists()) {
        const aiData = aiSnap.data() || {};
        const aiPrefs = aiData.suggestedPreferences || {};
        const aiWeights = aiData.recommendedWeights || {};
        const shouldApplyAI = !prefs.preferencesCompleted || !!aiData.forceApply;

        if (shouldApplyAI) {
          appliedAI = true;
          prefs = {
            ...prefs,
            ...aiPrefs,
            matchingWeights: Object.keys(aiWeights).length ? aiWeights : prefs.matchingWeights,
          };
        }
      }
    } catch {
      // ignore AI failures
    }

    return { prefs, appliedAI };
  }, [uid]);

  const buildSimilar = useCallback(
    (all) => {
      const meCity = safeLower(city);
      const meCountry = safeLower(country);

      const scored = all
        .filter((t) => t?.id && t.id !== tradeId)
        .filter(isProbablyAvailable)
        .map((t) => {
          let s = 0;

          const tCat = safeLower(t?.categoryNorm || t?.category || "");
          if (categoryNorm && tCat && tCat === categoryNorm) s += 4;
          else if (category && safeLower(t?.category || "") === safeLower(category)) s += 3;

          const tCity = safeLower(t?.location?.city || "");
          const tCountry = safeLower(t?.location?.country || "");

          if (preferSameCity && meCity && tCity && tCity === meCity) s += 3;
          if (meCountry && tCountry && tCountry === meCountry) s += 1;
          if (!meCountry && region && isCountryMatch(t, region)) s += 1;

          // Small nudge towards “priced” items (better conversion UX)
          if (pickCashValue(t) != null) s += 0.5;

          return { ...t, __score: s };
        });

      scored.sort((a, b) => (b.__score ?? 0) - (a.__score ?? 0));
      return scored.slice(0, maxSimilar);
    },
    [tradeId, categoryNorm, category, city, country, region, preferSameCity, maxSimilar]
  );

  const buildRecommended = useCallback(
    (all, prefs) => {
      // Guest fallback: trending (fresh + “priced”)
      if (!uid || !prefs) {
        const base = all
          .filter((t) => t?.id && t.id !== tradeId)
          .filter(isProbablyAvailable)
          .map((t) => {
            let s = 0;
            if (pickCashValue(t) != null) s += 2;
            if (region && isCountryMatch(t, region)) s += 1;
            return { ...t, __score: s };
          });
        base.sort((a, b) => (b.__score ?? 0) - (a.__score ?? 0));
        return base.slice(0, maxRecommended);
      }

      const weights = prefs.matchingWeights || DEFAULT_WEIGHTS;

      const preferredStyles = normalizeArr(prefs.preferredStyles).map(safeLower).filter(Boolean);
      const preferredBrands = normalizeArr(prefs.preferredBrands).map(safeLower).filter(Boolean);
      const favoriteCategories = normalizeArr(prefs.favoriteCategories).map(safeLower).filter(Boolean);
      const tradeTypes = normalizeArr(prefs.tradeType).map(safeLower).filter(Boolean);
      const prefCondition = normStr(prefs.preferredCondition || "");

      const prefLoc = prefs.location; // can be string or structured
      const prefCities = normalizeArr(prefLoc?.cities).map(safeLower).filter(Boolean);
      const prefLocText = typeof prefLoc === "string" ? safeLower(prefLoc) : "";

      const meCity = safeLower(city);
      const meCountry = safeLower(country);

      const matches = all
        .filter((t) => t?.id && t.id !== tradeId)
        .filter(isProbablyAvailable)
        .map((t) => {
          let score = 0;

          const reasons = [];

          // Category (strongest signal)
          const tCat = safeLower(t?.category || t?.categoryLabel || t?.categoryKey || "");
          const tCatNorm = safeLower(t?.categoryNorm || "");
          const catKey = tCatNorm || tCat;
          if (catKey && favoriteCategories.includes(catKey)) {
            score += weights.category;
            reasons.push("category");
          } else if (categoryNorm && (tCatNorm === categoryNorm || tCat === categoryNorm)) {
            // “Opened trade” similarity boost
            score += 1.5;
            reasons.push("category");
          }

          // Style/Brand (title matching)
          const title = safeLower(t?.title || "");
          if (preferredStyles.length && preferredStyles.some((s) => s && title.includes(s))) {
            score += weights.style;
            reasons.push("style");
          }
          if (preferredBrands.length && preferredBrands.some((b) => b && title.includes(b))) {
            score += weights.brand;
            reasons.push("brand");
          }

          // Condition
          if (!prefCondition || prefCondition === "Any" || normStr(t?.condition) === prefCondition) {
            score += weights.condition;
            reasons.push("condition");
          }

          // Trade type (kept as compatibility even in cash-only UI)
          const tt = safeLower(t?.tradeType || t?.tradePreference || "");
          if (tradeTypes.length && tradeTypes.includes(tt)) {
            score += weights.tradeType;
            reasons.push("tradeType");
          }

          // Location: prefer same-city to opened trade + user prefs
          const tCity = safeLower(t?.location?.city || "");
          const tCountry = safeLower(t?.location?.country || "");

          if (meCity && tCity && tCity === meCity) {
            score += weights.location + 1;
            reasons.push("location");
          } else if (prefCities.length && tCity && prefCities.includes(tCity)) {
            score += weights.location;
            reasons.push("location");
          } else if (prefLocText && safeLower(t?.location?.label || "").includes(prefLocText)) {
            score += weights.location;
            reasons.push("location");
          } else if (meCountry && tCountry && tCountry === meCountry) {
            score += Math.max(1, weights.location - 0.25);
            reasons.push("location");
          } else if (region && isCountryMatch(t, region)) {
            score += Math.max(1, weights.location - 0.25);
            reasons.push("location");
          }

          // Conversion nudge: priced items
          if (pickCashValue(t) != null) score += 0.75;

          return { ...t, matchScore: score, matchReasons: reasons, __score: score };
        });

      // Thresholds similar to your existing recommender
      let filtered = matches.filter((t) => (t.__score ?? 0) >= 3);
      if (filtered.length === 0) filtered = matches.filter((t) => (t.__score ?? 0) >= 1);
      if (filtered.length === 0) filtered = matches;

      filtered.sort((a, b) => (b.__score ?? 0) - (a.__score ?? 0));
      return filtered.slice(0, maxRecommended);
    },
    [uid, tradeId, region, categoryNorm, city, country, maxRecommended]
  );

  const refresh = useCallback(async () => {
    if (!tradeId) return;

    setLoading(true);
    try {
      const [all, prefBundle] = await Promise.all([fetchLatestPublicTrades(), loadPrefs()]);
      setUsedAISuggestions(!!prefBundle.appliedAI);

      const sims = buildSimilar(all);
      const recs = buildRecommended(all, prefBundle.prefs);

      setSimilar(sims);
      setRecommended(recs);
    } catch (e) {
      console.error("[SimilarTradesCarousel] load failed:", e);
      setSimilar([]);
      setRecommended([]);
      setUsedAISuggestions(false);
    } finally {
      setLoading(false);
    }
  }, [tradeId, fetchLatestPublicTrades, loadPrefs, buildSimilar, buildRecommended]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const show = useMemo(() => !loading && (similar.length > 0 || recommended.length > 0), [loading, similar, recommended]);

  if (!tradeId) return null;
  if (!show && !loading) return null;

  const Row = ({ which, rowTitle, rowSub, data, stripRef }) => (
    <div style={{ marginTop: which === "rec" ? 14 : 0 }}>
      <div className="sim-shelf__head nz-flex-wrap">
        <div>
          <div className="sim-shelf__title">{rowTitle}</div>
          <div className="sim-shelf__sub">{rowSub}</div>
        </div>

        <div className="sim-shelf__controls">
          <button type="button" className="sim-nav nz-touch-min" onClick={() => scroll(which, "left")} aria-label="Scroll left">
            ‹
          </button>
          <button type="button" className="sim-nav nz-touch-min" onClick={() => scroll(which, "right")} aria-label="Scroll right">
            ›
          </button>
        </div>
      </div>

      <div className="sim-strip" ref={stripRef}>
        {data.map((t) => {
          const cash = pickCashValue(t);
          const badge = cash ? moneyZAR(cash) : "";
          const loc = t?.location?.city
            ? `${t.location.city}${t.location?.country ? `, ${t.location.country}` : ""}`
            : "Location not set";

          const tag = normStr(t?.category || t?.categoryLabel || "General");
          const condition = normStr(t?.condition || "Used");

          return (
            <Link key={t.id} to={`/trade/${t.id}`} className="sim-card" title={t.title || "View trade"}>
              <div className="sim-card__imgWrap">
                <img src={getTradeImageSrc(t)} alt={t.title || "Trade"} onError={handleImgError} loading="lazy" />
                {badge ? <span className="sim-card__badge">{badge}</span> : null}
              </div>

              <div className="sim-card__body">
                <div className="sim-card__title">{t.title || "Untitled"}</div>

                <div className="sim-card__meta">
                  <span className="sim-tag">{tag}</span>
                  <span className="sim-dot">•</span>
                  <span className="sim-loc">{loc}</span>
                </div>

                <div className="sim-card__foot">
                  <span className="sim-muted">{condition}</span>
                  <span className="sim-cta">View →</span>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );

  return (
    <section className={`sim-shelf ${className}`}>
      <div className="sim-shelf__head nz-flex-wrap" style={{ marginBottom: 10 }}>
        <div>
          <div className="sim-shelf__title">{title}</div>
          <div className="sim-shelf__sub">
            {subtitle}
            {!uid ? " (Sign in to personalize)" : usedAISuggestions ? " (AI-assisted preferences applied)" : ""}
          </div>
        </div>

        <div className="sim-shelf__controls">
          <button type="button" className="sim-nav nz-touch-min" onClick={refresh} aria-label="Refresh">
            ↻
          </button>
        </div>
      </div>

      {loading ? (
        <div className="sim-strip" aria-busy="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="sim-card" style={{ minHeight: 240 }}>
              <div className="sim-card__imgWrap" />
              <div className="sim-card__body">
                <div className="sim-card__title" style={{ background: "rgba(15,23,42,0.06)", borderRadius: 10, height: 16 }} />
                <div style={{ marginTop: 10, background: "rgba(15,23,42,0.04)", borderRadius: 10, height: 12, width: "70%" }} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {similar.length > 0 && (
            <Row
              which="sim"
              rowTitle="Similar to this listing"
              rowSub="Category and location-matched options"
              data={similar}
              stripRef={simRef}
            />
          )}

          {recommended.length > 0 && (
            <Row
              which="rec"
              rowTitle={uid ? "Recommended for you" : "Trending now"}
              rowSub={uid ? "Based on your preferences and behavior signals" : "Fresh picks across the marketplace"}
              data={recommended}
              stripRef={recRef}
            />
          )}
        </>
      )}
    </section>
  );
};

export default SimilarTradesCarousel;